<template>
  <div :class="[$style.sidebar, { [$style.sp_open]: localValue }]">
    <AtomsConsumerButton :class="$style.close" @click="closeFilter" />
    <div :class="$style.filter_list">
      <p :class="$style.filter_title">絞り込み検索</p>

      <AtomsConsumerButton v-if="filterCountNum > 0" :class="$style.clear" @click="$emit('reset')">
        すべて解除
      </AtomsConsumerButton>
      <MoleculesConsumerAccordion
        icon="plus-minus"
        :is-open-by-default="!md || initialDetailOpenStatus.sex"
        :class="$style.accordion"
      >
        <template #title>性別</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <CommonMultiCheckBox
              v-model:value="localFilter.sex"
              name="sex"
              :list="sexItems"
              :is-required-text-form="false"
            />
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <MoleculesConsumerAccordion
        icon="plus-minus"
        :is-open-by-default="!md || initialDetailOpenStatus.silhouette"
        :class="$style.accordion"
      >
        <template #title>シルエット</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <CommonMultiCheckBox
              v-model:value="localFilter.silhouette"
              name="silhouette"
              :list="silhouetteItems"
              :is-required-text-form="false"
            />
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <MoleculesConsumerAccordion
        icon="plus-minus"
        :is-open-by-default="!md || initialDetailOpenStatus.wear_category"
        :class="$style.accordion"
      >
        <template #title>セット</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <CommonMultiCheckBox
              v-model:value="localFilter.wear_category"
              name="wearCategory"
              :list="wearCategoryItems"
              :is-required-text-form="false"
            />
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <MoleculesConsumerAccordion
        icon="plus-minus"
        :is-open-by-default="!md || initialDetailOpenStatus.size"
        :class="$style.accordion"
      >
        <template #title>サイズ</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <CommonMultiCheckBox
              v-model:value="localFilter.size"
              name="size"
              :list="sizeItems"
              :is-required-text-form="false"
            />
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <MoleculesConsumerAccordion
        icon="plus-minus"
        :is-open-by-default="!md || initialDetailOpenStatus.season"
        :class="$style.accordion"
      >
        <template #title>季節</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <CommonMultiCheckBox
              v-model:value="localFilter.season"
              name="season"
              :list="seasonItems"
              :is-required-text-form="false"
            />
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <MoleculesConsumerAccordion
        icon="plus-minus"
        :is-open-by-default="!md || initialDetailOpenStatus.fabric"
        :class="$style.accordion"
      >
        <template #title>素材</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <CommonMultiCheckBox
              v-model:value="localFilter.fabric"
              name="fabric"
              :list="fabricItems"
              :is-required-text-form="false"
            />
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <MoleculesConsumerAccordion
        v-if="isLinkCategory && categories"
        icon="plus-minus"
        is-open-by-default
        :class="$style.accordion"
      >
        <template #title>サブカテゴリー</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <AtomsConsumerLink
              v-for="category in categories"
              :key="category.slug"
              :url="`/${categorySlug}/${category.slug}`"
              :disabled="checkCurrentPage(category.slug)"
              :class="{ [$style.active]: checkCurrentPage(category.slug) }"
            >
              {{ category.name }}
            </AtomsConsumerLink>
          </div>
        </template>
      </MoleculesConsumerAccordion>
      <MoleculesConsumerAccordion v-else icon="plus-minus" is-open-by-default :class="$style.accordion">
        <template #title>カテゴリー</template>
        <template #content>
          <div :class="$style.checkbox_list">
            <AtomsConsumerRadioButton
              v-for="category in allListCategories"
              :key="category.document_id"
              :checked="localFilter.category ?? ''"
              name="category"
              :value="category.document_id || ''"
              :class="[$style.item, { [$style.active]: localFilter.category === category.document_id }]"
              @change="(v) => (localFilter.category = v)"
            >
              {{ category.name }}
            </AtomsConsumerRadioButton>
          </div>
        </template>
      </MoleculesConsumerAccordion>

      <div :class="$style.single_filter_buttons">
        <AtomsConsumerToggle
          :model-value="Boolean(localFilter.is_in_stock)"
          :disabled="!Boolean(filterCounts.is_in_stock)"
          @update:model-value="(v) => (localFilter.is_in_stock = v ? '1' : '0')"
        >
          在庫がある商品のみ表示
        </AtomsConsumerToggle>
        <AtomsConsumerToggle
          :model-value="Boolean(localFilter.is_outlet)"
          :disabled="!Boolean(filterCounts.is_outlet)"
          @update:model-value="(v) => (localFilter.is_outlet = v ? '1' : '0')"
        >
          アウトレット
        </AtomsConsumerToggle>
      </div>
    </div>
    <AtomsConsumerButton v-if="!md" :class="$style.submit" @click="closeFilter">
      結果を表示（{{ allCount }}件）
    </AtomsConsumerButton>
  </div>
  <div v-if="localValue && !md" :class="$style.overlay_area" @click="emit('update:isOpenFilterSp', false)" />
</template>

<script setup lang="ts">
import {
  FABRIC_ITEMS,
  SEASON_ITEMS,
  SEX_ITEMS,
  SIZE_ITEMS,
  WEAR_CATEGORY_ITEMS,
  SILHOUETTE_ITEMS,
  type ProductAllSearchInput_Sort,
} from "~/const/product"
import type { ProductFilterV2Props } from "~/types/product"

const props = defineProps<ProductFilterV2Props>()

const emit = defineEmits<{
  (e: "update:sort", value: ProductAllSearchInput_Sort): void
  (e: "update:filter", value: ProductFilterV2Props["filter"]): void
  (e: "reset"): void
  (e: "update:isOpenFilterSp", isOpenFilterSp: boolean): void
}>()

const localValue = computed({
  get: () => props.isOpenFilterSp,
  set: (value) => emit("update:isOpenFilterSp", value),
})

const closeFilter = (): void => {
  localValue.value = false
}

const { buildFilterFromQuery } = useProductsPage()
const localFilter = ref(buildFilterFromQuery(props.filter))

const { y } = useWindowScroll()
const { width: windowWidth } = useWindowSize()
const md = computed(() => windowWidth.value >= 768)
const filterRef = ref<HTMLElement>()
const route = useRoute()
const categorySlug = route.params.categorySlug

type DetailOpenStatus = {
  fabric: boolean
  season: boolean
  sex: boolean
  size: boolean
  wear_category: boolean
  silhouette: boolean
}

/**
 * 初期表示時 <details open> を制御する
 */
const initialDetailOpenStatus: DetailOpenStatus = {
  fabric: !md.value || !!localFilter.value.fabric.length,
  season: !md.value || !!localFilter.value.season.length,
  sex: !md.value || !!localFilter.value.sex.length,
  size: !md.value || !!localFilter.value.size.length,
  wear_category: !md.value || !!localFilter.value.wear_category.length,
  silhouette: !md.value || !!localFilter.value.silhouette.length,
}

type CommonMultiCheckBoxItem = {
  key: string
  value: string
  disabled?: boolean
}

const sexItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SEX_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`sex_${item.value}`],
  }))
})
const fabricItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return FABRIC_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`fabric_${item.value}`],
  }))
})
const seasonItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SEASON_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`season_${item.value}`],
  }))
})
const wearCategoryItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return WEAR_CATEGORY_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`wear_category_${item.value}`],
  }))
})
const silhouetteItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SILHOUETTE_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`silhouette_${item.value}`],
  }))
})
const sizeItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SIZE_ITEMS.map((item) => ({
    ...item,
    disabled: item.value !== "その他" ? !props.filterCounts[`size_${item.value}`] : !props.filterCounts.size_other,
  }))
})

const filterCountNum = computed<number>(() => {
  const filterByInStock = localFilter.value.is_in_stock ? 1 : 0
  const filterByInOutlet = localFilter.value.is_outlet ? 1 : 0

  return (
    filterByInStock +
    filterByInOutlet +
    localFilter.value.fabric.length +
    localFilter.value.season.length +
    localFilter.value.sex.length +
    localFilter.value.size.length +
    localFilter.value.wear_category.length +
    localFilter.value.silhouette.length
  )
})

const setFilterPosition = (): void => {
  const filterElement = filterRef.value
  const headerElement = document.getElementById("site-header-id")
  if (!filterElement || !headerElement) return
  const headerHeight = headerElement.clientHeight
  headerElement.classList.contains("hide")
    ? (filterElement.style.top = "0")
    : (filterElement.style.top = headerHeight + "px")
}

const checkCurrentPage = (urlKey: string): boolean => {
  const { path } = route
  const splitPathArray = path.split("/")
  if (!splitPathArray || !splitPathArray[2]) return false
  if (path.match(urlKey)) return true

  return false
}

watch(
  () => props.filter,
  (newValue) => {
    localFilter.value = buildFilterFromQuery(newValue)
  },
  { deep: true },
)

watch(
  () => localFilter.value,
  (newValue) => {
    if (JSON.stringify(buildFilterFromQuery(props.filter)) === JSON.stringify(localFilter.value)) return
    emit("update:filter", newValue)
  },
  { deep: true },
)

watch(
  () => props.isOpenFilterSp,
  (value) => {
    if (value) {
      window.history.pushState(null, "", null)
      window.addEventListener("popstate", closeFilter)
    } else {
      window.removeEventListener("popstate", closeFilter)
    }
  },
)

watch(y, () => {
  if (!md.value) setFilterPosition()
})
</script>

<style lang="scss" scoped module>
.sidebar {
  position: relative;
  flex-shrink: 0;
  width: 25%;
  max-width: 16rem;
  margin-right: 1.875rem;

  // Macでスクロールバーを常時表示
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 0.25rem;
    background-color: $black-10;
    -webkit-box-shadow: 0 0 1px $black-10;
  }

  .close {
    position: absolute;
    top: 30px;
    right: 30px;
    padding: 15px;
    cursor: pointer;
    &::before,
    &::after {
      position: absolute;
      top: 14px;
      left: 0px;
      width: 32px;
      height: 1px;
      content: "";
      background: $primary;
    }
    &::before {
      transform: rotate(45deg);
    }
    &::after {
      transform: rotate(-45deg);
    }
    @include md {
      display: none;
    }
  }
  .filter_list {
    max-height: calc(100vh - 3.75rem);
    max-height: calc(100dvh - 3.75rem);
    overflow-y: auto;
    padding: 2rem 1rem;
    @include md {
      max-height: 85vh;
      padding: 1rem 0;
    }
    .filter_title {
      @include main-font;
      font-size: $font-size-16;
      display: block;
      margin-bottom: 1rem;
    }
    .clear {
      color: $black;
      text-decoration: underline;
      margin-top: 0.5rem;
    }
    .sp_sort {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding-bottom: 1rem;
    }
    .list_item,
    .accordion {
      padding: 1rem 0;
      &:not(:last-child) {
        border-bottom: 1px solid $secondary-40;
      }
      .select_title {
        font-weight: bold;
      }
      .checkbox_list {
        display: flex;
        flex-direction: column;
        margin-top: 0.5rem;
        gap: 0.5rem;
        padding: 0.5rem 0;
        font-size: $font-size-14;
        .active {
          color: $primary;
          font-weight: bold;
          pointer-events: none;
        }
      }

      .item {
        font-weight: inherit;
        padding: 0.125rem;
      }
    }
    .single_filter_buttons {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 1rem 0;
      font-size: $font-size-14;
    }
  }
  .submit {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 1rem 0;
    background-color: $primary;
    color: $white;
    font-weight: bold;
    justify-content: center;
    text-align: center;
    cursor: pointer;
    z-index: 101;
  }

  @include sp {
    transform: translateX(-100%);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    width: 90vw;
    max-width: 300px;
    height: 100vh;
    height: 100dvh;
    overflow-y: auto;
    background-color: $white;

    transition: transform 0.3s ease;
    // フィルター背後の商品一覧スクロールを連動させない
    overscroll-behavior-y: none;
    &.sp_open {
      transform: translateX(0);
    }
  }
}

.overlay_area {
  position: fixed;
  inset: 0px;
  z-index: 99;
  width: 100%;
  height: 100%;
  background-color: rgba($primary, 0.85);
  cursor: pointer;
}
</style>
